// 스킬 트리 화면의 표시 모델(순수 로직): 노드 상태, 선택 노드 카드의 수치(위력·재사용·MP·패시브 효과), 머리 줄(Lv·EXP·포인트),
// 레벨업 띠 문구, 이번 판 EXP 요약. DOM·three를 모른다. 규칙·수치의 정본은 shared/growth.ts·shared/config.ts이고 여기서 하드코딩하지 않는다.
// 정본 문서: docs/SKILL-TREE.md §7.
import { SKILLS, TICK_HZ, heroSkillSpecs, ms, type SkillSpec } from '../shared/config';
import type { PlayableHeroId } from '../shared/types';
import { SKILL_FOCUS_COOLDOWN } from '../shared/rpg';
import {
  BRANCHES, BURST, LEARN_DENIAL_TEXT, LEVEL_MAX, NODE_COUNT, SKILL_NODES, TIER_LEVELS,
  activeRankLevel, growthMods, learnDenial, levelFromXp, makeGrowthMods, pointsAtLevel,
  type BranchDef, type BranchId, type GrowthMods, type LearnDenial, type SkillNodeDef,
} from '../shared/growth';
import type { GrowthView } from '../shared/types';

// ── 성장 뷰 접근 ────────────────────────────────────
/** GameView.growth. 게임 쪽 getter가 아직 없는 빌드에서도 UI가 죽지 않게 없으면 null */
export function growthOf(view: { readonly growth?: GrowthView }): GrowthView | null {
  return view.growth ?? null;
}

// ── 스킬 사양 ───────────────────────────────────────
/**
 * 노드가 강화하는 스킬의 기본 사양. 스킬 4(진홍 초승달)는 SKILLS 설정에 들어오기 전 빌드를 위해 기획서 §4 값(재사용 7초·MP 60·위력 280%)을 대신 쓴다.
 * SKILLS[3]이 생기면 그것이 항상 우선한다.
 */
const CRESCENT_SPEC: SkillSpec = { id: 4, key: 'L', name: '진홍 초승달', cooldown: ms(7000), mpCost: 55, mult: 2.8, radius: 2.4, duration: ms(550) };
export function skillSpecOf(skillId: number, hero: PlayableHeroId = 'kael', job: 0 | 1 = 0): SkillSpec | null {
  // c022: 카엘 외 영웅은 그 영웅 표(heroSkillSpecs)에서 찾는다. job = 전직 갈래(C10-5·6: 카엘 9·10, 라이라 12·13이 갈래마다 다르다)
  if (hero !== 'kael') return heroSkillSpecs(hero, job)[skillId - 1] ?? null;
  return SKILLS[skillId - 1] ?? (skillId === 4 ? CRESCENT_SPEC : heroSkillSpecs('kael', job)[skillId - 1] ?? null);
}

// ── 노드 상태 ───────────────────────────────────────
/**
 * maxed: 최고 랭크(빛남) / learned: 1랭크 이상(빛남) / open: 아직 안 배웠지만 층·선행은 열림(포인트나 랭크 레벨이 모자랄 수 있음) / locked: 영웅 레벨 또는 선행이 모자라 잠김
 */
export type NodeState = 'maxed' | 'learned' | 'open' | 'locked';
export interface TreeNode {
  readonly index: number;
  readonly def: SkillNodeDef;
  readonly rank: number;
  readonly state: NodeState;
  /** 지금 한 랭크 올릴 수 있다(맥동 표시, 배우기 버튼 활성) */
  readonly learnable: boolean;
  readonly denial: LearnDenial | null;
  /** 잠긴 이유. 잠기지 않았으면 null */
  readonly lock: 'level' | 'prereq' | null;
  /** 노드 아래 라벨: 잠기면 'Lv.N'(층 해금 레벨), 선행 부족이면 '선행' */
  readonly lockLabel: string;
  /** 같은 가지 바로 위 층으로 이어지는 연결선이 금색인지(이 노드가 1랭크 이상) */
  readonly linkUp: boolean;
}

export function treeNodes(ranks: ArrayLike<number>, level: number): TreeNode[] {
  const out: TreeNode[] = [];
  for (let i = 0; i < NODE_COUNT; i++) {
    const def = SKILL_NODES[i];
    const rank = ranks[i] | 0;
    const denial = learnDenial(ranks, level, i);
    const lock = rank === 0 && (denial === 'level' || denial === 'prereq') ? denial : null;
    const state: NodeState = rank >= def.maxRank ? 'maxed' : rank >= 1 ? 'learned' : lock ? 'locked' : 'open';
    out.push({
      index: i, def, rank, state, learnable: denial === null, denial, lock,
      lockLabel: lock === 'level' ? 'Lv.' + TIER_LEVELS[def.tier] : lock === 'prereq' ? '선행' : '',
      linkUp: rank >= 1,
    });
  }
  return out;
}

/** 가지의 노드(층 오름차순) */
export function branchNodes(nodes: readonly TreeNode[], branch: BranchId): TreeNode[] {
  return nodes.filter((n) => n.def.branch === branch).sort((a, b) => a.def.tier - b.def.tier);
}

/** 처음 열 때 고를 노드: 지금 배울 수 있는 노드 중 가장 낮은 층(같으면 가지 순서), 없으면 첫 노드 */
export function defaultNode(nodes: readonly TreeNode[]): number {
  let best = -1;
  for (const n of nodes) {
    if (!n.learnable) continue;
    if (best < 0 || n.def.tier < nodes[best].def.tier) best = n.index;
  }
  return best >= 0 ? best : 0;
}

export function branchDef(id: BranchId): BranchDef {
  return BRANCHES.find((b) => b.id === id) ?? BRANCHES[0];
}

// ── 머리 줄 ─────────────────────────────────────────
export interface TreeHeader {
  readonly level: number;
  readonly maxLevel: boolean;
  /** 'xp - 레벨시작 / 다음 - 레벨시작' 형태(최고 레벨이면 'MAX') */
  readonly xpText: string;
  /** 0..1 */
  readonly xpFrac: number;
  readonly points: number;
  readonly spent: number;
  /** 아직 쓰지 않은 포인트 */
  readonly free: number;
}
export function treeHeader(g: Pick<GrowthView, 'level' | 'xp' | 'xpLevelStart' | 'xpNext' | 'points' | 'spent'>): TreeHeader {
  const maxLevel = g.level >= LEVEL_MAX;
  const span = g.xpNext - g.xpLevelStart;
  const have = Math.max(0, g.xp - g.xpLevelStart);
  return {
    level: g.level, maxLevel,
    xpText: maxLevel || span <= 0 ? 'MAX' : have + ' / ' + span,
    xpFrac: maxLevel || span <= 0 ? 1 : Math.max(0, Math.min(1, have / span)),
    points: g.points, spent: g.spent, free: Math.max(0, g.points - g.spent),
  };
}

// ── 장비 미리보기용 성장 보정 ─────────────────────
/**
 * 장비·개선 미리보기(deriveStats)에 넘길 GrowthMods. 실제 장착 뒤 능력치(game.gmods)와 같게 view.growth의 레벨·ranks로 계산한다.
 * 레벨·랭크가 바뀔 때(revision)만 다시 계산하고 같은 객체를 돌려준다(프레임마다 할당하지 않는다). 성장 정보가 없으면 undefined(보정 없음).
 */
export class GrowthModsCache {
  private readonly mods = makeGrowthMods();
  private rev = -1;
  private lv = -1;
  get(g: Pick<GrowthView, 'revision' | 'level' | 'ranks'> | null): GrowthMods | undefined {
    if (!g) return undefined;
    if (g.revision !== this.rev || g.level !== this.lv) {
      this.rev = g.revision; this.lv = g.level;
      growthMods(this.mods, g.level, g.ranks);
    }
    return this.mods;
  }
}

// ── 선택 노드 카드 ──────────────────────────────────
export interface CardRow {
  readonly label: string;
  readonly from: string;
  readonly to: string;
  /** from과 to가 다르다(강조 표시) */
  readonly changed: boolean;
}
export interface NodeCard {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  readonly branch: BranchId;
  readonly kindLabel: string;
  readonly skillId: number;
  readonly rank: number;
  readonly maxRank: number;
  /** 배운 뒤 랭크(최고 랭크면 null) */
  readonly nextRank: number | null;
  /** 'Lv 2 → 3', 미습득 '미습득 → Lv 1', 최고 'Lv 5 (최고)' */
  readonly levelText: string;
  readonly desc: string;
  /** 5랭크(액티브)·캡스톤 추가 효과 이름. 없으면 '' */
  readonly bonusName: string;
  readonly bonusOn: boolean;
  /** 액티브 다음 랭크가 요구하는 영웅 레벨(패시브·최고 랭크면 0) */
  readonly needHeroLevel: number;
  readonly rows: readonly CardRow[];
  readonly denial: LearnDenial | null;
  readonly denialText: string;
  readonly canLearn: boolean;
  /** 주력 스킬로 삼을 수 있는 노드(스킬 1~3 액티브이고 배운 상태) */
  readonly focusable: boolean;
}

/** 소수 한 자리까지(정수면 정수) */
function num(v: number): string {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}
/** 위력은 정수 %로 반올림한다(297.6% 같은 소수 표기는 읽기 어렵다) */
const pct = (v: number): string => Math.round(v * 100) + '%';
const signed = (v: number, plus: string, unit: string): string => (v === 0 ? '—' : plus + num(v) + unit);
const secs = (ticks: number): string => num(ticks / TICK_HZ) + '초';

function ranksWith(ranks: ArrayLike<number>, node: number, rank: number): Uint8Array {
  const out = new Uint8Array(NODE_COUNT);
  for (let i = 0; i < NODE_COUNT; i++) out[i] = ranks[i] | 0;
  out[node] = rank;
  return out;
}

/** 한 노드가 랭크 a → b가 될 때 바뀌는 수치 줄. growthMods를 두 번 돌려 차이를 읽는다(여기에 수치를 따로 적지 않는다) */
function cardRows(def: SkillNodeDef, ranks: ArrayLike<number>, level: number, a: number, b: number, skillFocus: number): CardRow[] {
  const ma = growthMods(makeGrowthMods(), level, ranksWith(ranks, SKILL_NODES.indexOf(def), a));
  const mb = growthMods(makeGrowthMods(), level, ranksWith(ranks, SKILL_NODES.indexOf(def), b));
  const rows: CardRow[] = [];
  const add = (label: string, from: string, to: string): void => { rows.push({ label, from, to, changed: from !== to }); };
  switch (def.effect) {
    case 'skillDamage': {
      const spec = skillSpecOf(def.skillId);
      if (!spec) break;
      const i = def.skillId - 1;
      add('피해량', a === 0 ? '—' : pct(spec.mult * ma.skillMult[i]), pct(spec.mult * mb.skillMult[i]));
      // deriveStats와 같은 식: 기본 × 주력(이 스킬이 주력일 때만) × 질풍, 틱 단위로 반올림
      const focus = skillFocus === def.skillId ? SKILL_FOCUS_COOLDOWN : 1;
      add('재사용 대기', secs(Math.round(spec.cooldown * focus * ma.cooldownMult)), secs(Math.round(spec.cooldown * focus * mb.cooldownMult)));
      add('소모 MP', String(spec.mpCost), String(spec.mpCost));
      break;
    }
    case 'critChance': add('치명타 확률', signed(ma.critAdd * 100, '+', '%'), signed(mb.critAdd * 100, '+', '%')); break;
    case 'skillLifesteal': add('스킬 처치 회복', signed(ma.lifesteal * 100, '최대 HP ', '%'), signed(mb.lifesteal * 100, '최대 HP ', '%')); break;
    case 'basicDamage': add('기본 공격 피해', signed((ma.basicMult - 1) * 100, '+', '%'), signed((mb.basicMult - 1) * 100, '+', '%')); break;
    case 'cooldown': add('스킬 재사용 대기', signed((1 - ma.cooldownMult) * 100, '−', '%'), signed((1 - mb.cooldownMult) * 100, '−', '%')); break;
    case 'linkDamage': add('연계 2·3단 피해', signed(ma.linkAdd * 100, '+', '%'), signed(mb.linkAdd * 100, '+', '%')); break;
    case 'maxHp': add('최대 HP', signed((ma.maxHpMult - 1) * 100, '+', '%'), signed((mb.maxHpMult - 1) * 100, '+', '%')); break;
    case 'mana':
      add('MP 재생', signed((ma.mpRegenMult - 1) * 100, '+', '%'), signed((mb.mpRegenMult - 1) * 100, '+', '%'));
      add('최대 MP', signed(ma.maxMpAdd, '+', ''), signed(mb.maxMpAdd, '+', ''));
      break;
    case 'damageTaken': add('받는 피해', signed((1 - ma.damageTakenMult) * 100, '−', '%'), signed((1 - mb.damageTakenMult) * 100, '−', '%')); break;
    case 'bloom':
      add('폭발 반경', a === 0 ? '—' : num(BURST.bloom.radius) + 'm', num(BURST.bloom.radius) + 'm');
      add('폭발 위력', a === 0 ? '—' : pct(BURST.bloom.mult), pct(BURST.bloom.mult));
      break;
    case 'thunder':
      add('검기 길이', a === 0 ? '—' : num(BURST.thunder.length) + 'm', num(BURST.thunder.length) + 'm');
      add('검기 위력', a === 0 ? '—' : pct(BURST.thunder.mult), pct(BURST.thunder.mult));
      break;
    case 'resonance':
      add('각성 게이지 획득', signed((ma.awakenGainMult - 1) * 100, '+', '%'), signed((mb.awakenGainMult - 1) * 100, '+', '%'));
      add('각성 피해', signed((ma.awakenDamageMult - 1) * 100, '+', '%'), signed((mb.awakenDamageMult - 1) * 100, '+', '%'));
      break;
  }
  return rows;
}

const KIND_LABEL: Record<SkillNodeDef['kind'], string> = { active: '액티브', passive: '패시브', capstone: '캡스톤' };

/** skillFocus: 지금 주력 스킬(view.profile.skillFocus, 0 = 없음). 주력이면 재사용 대기 미리보기에 주력 배율이 들어간다 */
export function nodeCard(ranks: ArrayLike<number>, level: number, node: number, skillFocus = 0): NodeCard {
  const def = SKILL_NODES[node] ?? SKILL_NODES[0];
  const idx = SKILL_NODES[node] ? node : 0;
  const rank = ranks[idx] | 0;
  const nextRank = rank >= def.maxRank ? null : rank + 1;
  const denial = learnDenial(ranks, level, idx);
  const shown = nextRank ?? rank;
  const rows = cardRows(def, ranks, level, nextRank === null ? Math.max(0, rank - 1) : rank, shown, skillFocus);
  // 최고 랭크: 이전 랭크 → 현재가 아니라 현재 값만 보여 준다(from=to)
  const fixed = nextRank === null ? rows.map((r) => ({ ...r, from: r.to, changed: false })) : rows;
  return {
    index: idx, id: def.id, name: def.name, branch: def.branch, kindLabel: KIND_LABEL[def.kind], skillId: def.skillId,
    rank, maxRank: def.maxRank, nextRank,
    levelText: nextRank === null ? 'Lv ' + rank + ' (최고)' : rank === 0 ? '미습득 → Lv 1' : 'Lv ' + rank + ' → ' + nextRank,
    desc: def.desc,
    bonusName: def.kind === 'capstone' ? '' : def.bonusName ?? '',
    bonusOn: def.kind !== 'capstone' && !!def.bonusName && rank >= def.maxRank,
    needHeroLevel: def.kind === 'active' && nextRank !== null ? activeRankLevel(nextRank) : 0,
    rows: fixed,
    denial, denialText: denial ? LEARN_DENIAL_TEXT[denial] : '',
    canLearn: denial === null,
    focusable: def.kind === 'active' && def.skillId >= 1 && def.skillId <= 3 && rank >= 1,
  };
}

/** 노드 카드의 "배우기 ✦N" 비용(항상 1포인트) */
export const LEARN_COST = 1;

// ── 레벨업 띠 ───────────────────────────────────────
/** 이번 레벨업으로 늘어난 스킬 포인트(여러 단계를 한 번에 올라도 합산) */
export function levelUpGain(level: number, steps: number): number {
  const from = Math.max(1, level - Math.max(1, steps));
  return pointsAtLevel(level) - pointsAtLevel(from);
}
/** 띠 문구를 둘로: 머리 'LEVEL UP'(금색)과 꼬리 'Lv.N · 스킬 포인트 +k' */
export function levelUpParts(level: number, steps: number): { head: string; tail: string } {
  return { head: 'LEVEL UP', tail: 'Lv.' + level + ' · 스킬 포인트 +' + levelUpGain(level, steps) };
}
export function levelUpText(level: number, steps: number): string {
  const p = levelUpParts(level, steps);
  return p.head + '  ' + p.tail;
}

// ── 이번 판 EXP 요약 ────────────────────────────────
export interface ExpSummary {
  readonly gained: number;
  readonly fromLevel: number;
  readonly toLevel: number;
}
/**
 * 던전에 들어간 순간의 확정 EXP를 기억해 두었다가 결과·패배 화면에서 "EXP +X (Lv.a → Lv.b)"를 만든다.
 * xp는 확정 + 이번 판 EXP(GrowthView.xp)이므로 저장 시점이 바뀌어도(pendingXp가 확정으로 옮겨져도) 값이 변하지 않는다.
 */
export class ExpRunTracker {
  private serial = -1;
  private startXp = -1;
  /** 던전 장면에서 매 프레임 불러도 된다(같은 장면 번호면 아무것도 하지 않는다) */
  observe(inDungeon: boolean, sceneSerial: number, xp: number, pendingXp: number): void {
    if (!inDungeon || sceneSerial === this.serial) return;
    this.serial = sceneSerial;
    this.startXp = Math.max(0, xp - Math.max(0, pendingXp));
  }
  reset(): void { this.serial = -1; this.startXp = -1; }
  /** 아직 던전을 못 봤으면 null */
  summary(xp: number): ExpSummary | null {
    if (this.startXp < 0) return null;
    return { gained: Math.max(0, Math.round(xp - this.startXp)), fromLevel: levelFromXp(this.startXp), toLevel: levelFromXp(xp) };
  }
}
export function expLine(s: ExpSummary | null): string {
  if (!s) return '';
  const base = 'EXP +' + s.gained.toLocaleString('en-US');
  return s.toLevel > s.fromLevel ? base + ' (Lv.' + s.fromLevel + ' → Lv.' + s.toLevel + ')' : base;
}

// ── EXP 저장 상태 ───────────────────────────────────
/** 보상 저장이 끝났어도 이번 판 EXP가 아직 저장되지 않았고 마지막 저장이 실패했으면 true(재시도 중) */
export function expSavePending(g: Pick<GrowthView, 'pendingXp' | 'lastSaveOk'> | null): boolean {
  return !!g && g.pendingXp > 0 && !g.lastSaveOk;
}
export const EXP_SAVE_WARNING = 'EXP 저장 대기 · 자동으로 재시도하고 있어요.\n저장되기 전에 새로고침하거나 닫으면 이번 판 EXP를 잃을 수 있어요.';
/** 보상 저장 문구가 이미 '저장 대기' 경고일 때 덧붙이는 한 줄 */
export const EXP_SAVE_WARNING_SHORT = 'EXP도 저장 대기 중이에요. 저장 전에 닫으면 이번 판 EXP를 잃을 수 있어요.';
/** 패배 화면: 이번 판 EXP 줄. 저장이 끝나기 전에는 '남아요'라고 말하지 않고 경고한다 */
export function defeatExpLine(exp: string, savePending: boolean): string {
  if (!exp) return '';
  return savePending ? exp + ' · EXP 저장 대기 중이에요. 저장 전에 닫으면 잃을 수 있어요' : exp + ' · 패배해도 EXP는 남아요';
}

// ── 마을 안내 점 ────────────────────────────────────
/** 쓰지 않은 스킬 포인트가 있는가(가방 버튼·교관 안내의 금색 점) */
export function hasUnspentPoints(g: Pick<GrowthView, 'points' | 'spent'> | null): boolean {
  return !!g && g.points - g.spent > 0;
}

